import axios from "axios";
import { createContext, useState, useContext, useEffect } from "react";
import i18n from './i18n';

const baseUrl = process.env.REACT_APP_API_URL || 'https://servicedeskapi.wello.solutions';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    try {
      const storedAuth = localStorage.getItem("auth");
      return storedAuth ? JSON.parse(storedAuth) : null;
    } catch (error) {
      console.error("Failed to parse auth data from local storage:", error);
      return null;
    }
  });

  const [colorPrimary, setColorPrimary] = useState(() => {
    try {
      return (window.welloServiceDesk && window.welloServiceDesk.color_primary) || null;
    } catch (e) {
      return null;
    }
  });

  useEffect(() => {
    if (window.welloServiceDesk) {
      const { color_primary } = window.welloServiceDesk || {};
      setColorPrimary(color_primary || null);
    }
  }, []);

  // Sync logout across tabs
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "auth" && event.newValue === null) {
        setAuth(null);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // const login = async (token, email, password) => {
  //   try {
  //     if (!token) throw new Error("Your access token is not set.");
  //     if (!email || !password) throw new Error("All fields must be filled out.");

  //     const authEmail = email.trim();
  //     const url = `${baseUrl.replace(/\/$/, '')}/api/Authentication/contact-authtoken/`;
  //     const data = { useremail: email, password, access_token: token };

  //     const response = await axios.post(url, data);

  //     if (response?.data) {
  //       const { firstname, lastname, id, db_language_iso_code, auth_token } = response.data;
  //       const authData = {
  //         authKey: auth_token,
  //         userId: id,
  //         userName: `${firstname} ${lastname}`,
  //         authEmail,
  //         userLang: db_language_iso_code
  //       };
  //       setAuth(authData);
  //       localStorage.setItem("auth", JSON.stringify(authData)); // ✅ use localStorage
  //     }

  //     return response;
  //   } catch (error) {
  //     const apiMessage =
  //       error?.response?.data?.message ||
  //       error?.response?.data?.error ||
  //       error?.message ||
  //       "Login failed. Please check your credentials.";
  //     throw new Error(apiMessage);
  //   }
  // };

  const login = async (domain, email, password) => {
    try {
      if (!domain || !email || !password) {
        throw new Error("All fields must be filled out.");
      }

      const authString = `${email.trim()}:${password.trim()}@${domain.trim()}`;
      const authKey = btoa(authString);
      const authEmail = email.trim();

      const url = `${baseUrl}/api/Contact?$filter=e_login+eq+'${encodeURIComponent(email)}'`;
    
      const response = await axios.get(url, {
        headers: {
          Authorization: `Basic ${authKey}`,
          Accept: "application/json",
        },
      });

      if (response.data) {
        const userName = response.data.value[0].firstname+' '+response.data.value[0].lastname;
        const userId = response.data.value[0].id;
        const db_language_iso_code = response.data.value[0].db_language_iso_code;
        console.log("Login response data:", response.data);
        // const authData = { authKey, userName, authEmail };
        const authData = {
          authKey: authKey,
          userId: userId,
          userName: userName,
          authEmail,
          userLang: db_language_iso_code
        };
        setAuth(authData);
        localStorage.setItem("auth", JSON.stringify(authData));
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw new Error("Login failed. Please check your credentials.");
    }
  };

  const logout = () => {
    setAuth(null);
    localStorage.removeItem("auth"); // ✅ use localStorage
  };

  const updateAuthToken = (newToken) => {
    setAuth(prev => {
      const updated = { ...prev, authKey: newToken };
      localStorage.setItem("auth", JSON.stringify(updated)); // ✅ persist update
      return updated;
    });
  };

  useEffect(() => {
    if (auth?.userLang) {
      try {
        const lang = auth.userLang;
        i18n.changeLanguage(lang);
        localStorage.setItem('i18nextLng', lang);
      } catch (e) {
        console.error("Failed to set language from auth:", e);
      }
    }
  }, [auth]);

  return (
    <AuthContext.Provider value={{ auth, login, logout, updateAuthToken, color_primary: colorPrimary }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};